/**
 * @file firmware.ts
 * @description GCOME_AP 磁轴键盘固件版本读取与固件升级实现。
 *
 * 固件版本读取（AP 协议 GetInfo, CMD=0x03）：
 *   基本信息区 32 字节，偏移 0-7 为有效标志，之后：
 *   偏移 8-9   — 版本号（次版本、主版本）
 *   偏移 10-11 — RF 版本号（次版本、主版本）
 *   偏移 12-13 — 模组版本号（次版本、主版本）
 *   例如 03 01 表示 1.03；FF FF 表示该接口不存在。
 *
 * 固件升级（IAP/ISP 原始包协议，详见 docs/果来微磁轴键盘固件升级协议.md）：
 *   进入 boot → 擦除 → 写入 → 校验 → 成功 → 结束
 *   APP 态进 Boot 发 64 字节包；Boot 态 IAP 发 512 字节 HID 报告（非 AP 帧），
 *   有效布局：命令码(1) + 数据长度(1) + 地址(4, 低字节在前) + 32 字节数据。
 *   一应一答模式：应答包 buffer[1] 不等于 0 表示校验失败/错误码。
 */

import { delay } from '../../../../utils/TimeUtils'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { FirmwareFeature } from '../../../../features/firmware/contract'
import {
  FIRMWARE_AUTH_CANCELLED,
  getFirmwareHostHooks,
  isFirmwareAuthCancelled,
} from '../../../../features/firmware/hostHooks'
import {
  DeviceType,
  VersionType,
  type RemoteDeviceVersion,
  type VersionInfo,
} from '../../../../types/firmware'
import type { GcomeApKeyboardDevice } from '../../../../devices/keyboard/gcome-ap/device'
import { GCOME_AP_CMD, GCOME_AP_IAP, GCOME_AP_REPORT } from '../../protocols/cmd'
import { processZip } from '../../../shared/firmware/shared/FileUtil'
import { BaseDevice } from '../../../../runtime/BaseDevice'
import { FirmwareEvents } from '../../../../events/FirmwareEvents'

/** 线上版本描述文件名（与 CH585M 等设备统一约定）。 */
const versionFileName = 'version.json'

/**
 * 进 Boot 后缺少 WebHID 授权（宿主未注入 hooks 或用户未选中 Upgrader）。
 * 正常 Web 路径应由 setFirmwareHostHooks 弹窗解决，不再要求页面调用第二接口。
 */
export const GCOME_AP_BOOT_AUTH_REQUIRED = 'GCOME_AP_BOOT_AUTH_REQUIRED'

/** @deprecated 已并入 updateFirmware + FirmwareHostHooks，保留常量以免旧日志误匹配 */
export const GCOME_AP_ENTER_BOOT_REQUIRED = 'GCOME_AP_ENTER_BOOT_REQUIRED'

/** 0x83 成功之后发 0x84，设备常立刻离开 Boot；超时/无应答应视为升级已完成。 */
function isBenignIapEndDisconnect(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /Device update end/i.test(message) && /no response|timeout/i.test(message)
}

function messageIncludes(error: unknown, token: string): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return message.includes(token)
}

/**
 * 将协议中的版本号低位、版本号高位转为可读字符串。
 *
 * 两个字节是独立的十六进制版本分量，不是需要换算的 u16 十进制数值：
 *   [0x03, 0x01] -> "1.03"
 *   [0x22, 0x01] -> "1.22"
 *
 * [0xFF, 0xFF] 表示不存在。
 */
function formatFirmwareVersion(minor: number, major: number): string | null {
  if (minor === 0xff && major === 0xff) return null
  const minorText = minor.toString(16).padStart(2, '0')
  return `${major.toString(16)}.${minorText}`
}

export class GcomeApKeyboardFirmware extends FirmwareFeature {
  private firmwareBuffer: Uint8Array = new Uint8Array(0)
  private startAddress: number = 0x00000000
  private lastResponse: Uint8Array | null = null
  private updateDevice: BaseDevice | null = null
  /**
   * 升级阶段：
   * - idle：无会话
   * - prepared：BIN 已就绪，尚未 enterBoot
   * - in_boot：已进 Boot，等待授权或烧录中
   */
  private updatePhase: 'idle' | 'prepared' | 'in_boot' = 'idle'

  // ========================= 公开接口 =========================

  /**
   * 读取固件版本信息。
   *
   * 通过 AP 协议 GetInfo 命令获取原始响应数据，从中解析三个版本号：
   *   data[0..1] — 主控版本号（次版本、主版本）
   *   data[2..3] — RF 版本号（次版本、主版本）
   *   data[4..5] — 模组版本号（次版本、主版本）
   *
   * 若某个版本号为 0xFFFF 表示该接口不存在，跳过不返回。
   */
  async getFirmwareVersion(): Promise<VersionInfo[]> {
    const device = this.device as unknown as GcomeApKeyboardDevice
    const res = await device.sendRawApCommand({ command: GCOME_AP_CMD.getInfo })
    const data = res.response.data
    const mainVersion = formatFirmwareVersion(data[0], data[1])
    const rfVersion = formatFirmwareVersion(data[2], data[3])
    const moduleVersion = formatFirmwareVersion(data[4], data[5])

    const result: VersionInfo[] = []

    if (mainVersion) {
      result.push({
        version: mainVersion,
        versionType: VersionType.MasterControl,
        deviceType: DeviceType.Keyboard,
      })
    }
    if (rfVersion) {
      result.push({
        version: rfVersion,
        versionType: VersionType.RF,
        deviceType: DeviceType.Keyboard,
      })
    }
    if (moduleVersion) {
      result.push({
        version: moduleVersion,
        versionType: VersionType.USB,
        deviceType: DeviceType.Keyboard,
      })
    }

    return result
  }

  private async getVersionInfo(): Promise<RemoteDeviceVersion> {
    // todo 本地/测试环境开发使用
    if (import.meta.env.VITE_BUILD_FLAVOR === 'test') {
      this.device.__setFirmwareBaseUrl('https://soft.rapoo.com/RapooDriver/FirmwareTest')
    }
    const model = this.device.manifest.mainControl ?? ''
    const timestamp = Date.now()
    const versionUrl = `${this.device.getFirmwareBaseUrl()}/${model}/${versionFileName}?t=${timestamp}`
    const versionRes = await fetch(versionUrl)
    if (!versionRes.ok) {
      throw new DeviceError(DeviceErrorCode.TIMEOUT, 'checkFirmwareUpdate failed')
    }
    const versionData = await versionRes.json()
    if (!versionData.Device || !versionData.Device.length) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'checkFirmwareUpdate failed')
    }
    return versionData.Device[0]
  }

  async checkFirmwareUpdate(): Promise<VersionInfo[]> {
    const versions = await this.getFirmwareVersion()

    // 这里写死，因为 GCOME_AP 目前只有主控芯片要更新
    const masterControl = versions.find(
      (version) => version.versionType === VersionType.MasterControl,
    )
    const masterControlVersion = masterControl?.version
    if (!masterControlVersion) {
      return versions.map((v) => ({ ...v, canUpdate: false }))
    }
    const versionFileInfo = await this.getVersionInfo()
    const version = versionFileInfo.FirmwareVersion
    if (!version) {
      return versions.map((v) => ({ ...v, canUpdate: false }))
    }
    const canUpdate = this.canUpdateByVersion(version, masterControlVersion)

    return versions.map((v) => ({
      ...v,
      onlineVersion: v.versionType === VersionType.MasterControl ? version : undefined,
      canUpdate: v.versionType === VersionType.MasterControl ? canUpdate : false,
      // todo 放开可不受限升级
      // canUpdate: true,
    }))
  }

  /**
   * 固件升级完整编排（页面只调本方法）：
   * - APP：下载 → 宿主弹窗确认进 Boot → enterBoot →（如需）授权 → IAP
   * - 主通道已是 update（Boot 恢复）：下载 → 跳过进 Boot → 主通道即 IAP 口（缺连接时再授权）
   * WebHID 授权 UI 由 setFirmwareHostHooks 注入；测试可 mock hooks 立即 resolve。
   */
  async updateFirmware(): Promise<void> {
    this.updatePhase = 'idle'
    this.updateDevice = null
    let updateError: DeviceError | null = null
    /** 已进 Boot 且仍缺授权时，不要 release 到一半；保留会话意义不大，但勿清 buffer 误导重试 */
    let holdForBootAuth = false
    const resumeFromBoot = this.device.isFirmwareUpdateMode()

    try {
      const versionFileInfo = await this.getVersionInfo()
      const FirmwareURL = versionFileInfo.FirmwareURL
      if (!FirmwareURL) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'updateFirmware failed')
      }

      const model = this.device.manifest.mainControl ?? ''
      const url = `${this.device.getFirmwareBaseUrl()}/${model}/${FirmwareURL}`
      const fileMap = await processZip(url)
      const firmwareEntries = Object.values(fileMap).filter(({ extension }) => extension === '.bin')
      if (firmwareEntries.length !== 1 || firmwareEntries[0].data.byteLength === 0) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'GCOME_AP 固件包中缺少有效 BIN 文件')
      }
      this.firmwareBuffer = firmwareEntries[0].data
      this.updatePhase = 'prepared'
      this.reportProgress(2)

      if (resumeFromBoot) {
        // 已在 Boot：跳过确认进 Boot / enterBoot；主通道即升级口
        this.updatePhase = 'in_boot'
        this.reportProgress(5)
        if (this.device.channel.isConnected) {
          this.updateDevice = this.device
        } else {
          await this.promptFirmwareAuth({
            kind: 'authorizeBootDevice',
            boot: { label: this.resolveBootPromptLabel() },
            allowCancel: false,
          })
          this.updateDevice = await this.device.session.channels.openDevice(
            { usageType: 'update' },
            { requestIfMissing: true },
          )
        }
        if (!this.updateDevice) {
          holdForBootAuth = true
          const boot = this.resolveBootIdentityFromManifest()
          throw new DeviceError(
            DeviceErrorCode.OPEN_FAILED,
            `${GCOME_AP_BOOT_AUTH_REQUIRED}: 未打开 Bootloader（0x${boot.vendorId.toString(16)}:0x${boot.productId.toString(16)}）。请在系统选择器中勾选升级设备；取消后只能拔插恢复`,
          )
        }
      } else {
        // 弹窗只传产品名示意；Boot VID/PID 仅从本机 manifest update filter 解析，不下发 Web
        await this.promptFirmwareAuth({
          kind: 'confirmEnterBoot',
          boot: { label: this.resolveBootPromptLabel() },
          allowCancel: true,
        })

        await this.enterBootLoader()
        this.updatePhase = 'in_boot'
        this.reportProgress(5)

        this.updateDevice = await this.openUpdateDeviceWithRetry({ requestIfMissing: false })
        if (!this.updateDevice) {
          await this.promptFirmwareAuth({
            kind: 'authorizeBootDevice',
            boot: { label: this.resolveBootPromptLabel() },
            allowCancel: false,
          })
          // 必须紧接在弹窗主按钮 resolve 之后，保留用户手势
          this.updateDevice = await this.device.session.channels.openDevice(
            { usageType: 'update' },
            { requestIfMissing: true },
          )
        }
        if (!this.updateDevice) {
          holdForBootAuth = true
          const boot = this.resolveBootIdentityFromManifest()
          throw new DeviceError(
            DeviceErrorCode.OPEN_FAILED,
            `${GCOME_AP_BOOT_AUTH_REQUIRED}: 未打开 Bootloader（0x${boot.vendorId.toString(16)}:0x${boot.productId.toString(16)}）。请在系统选择器中勾选升级设备；取消后只能拔插恢复`,
          )
        }
      }

      await this.runIapFlashPipeline()
      this.updatePhase = 'idle'
      this.firmwareBuffer = new Uint8Array(0)
    } catch (error) {
      console.error(error)
      if (isFirmwareAuthCancelled(error)) {
        this.updatePhase = 'idle'
        this.firmwareBuffer = new Uint8Array(0)
        updateError = new DeviceError(
          DeviceErrorCode.INVALID_PARAM,
          `${FIRMWARE_AUTH_CANCELLED}: 已取消固件升级`,
          error,
        )
      } else if (isBenignIapEndDisconnect(error)) {
        console.warn('[GCOME_AP] ignoring benign IAP end disconnect', error)
        updateError = null
        this.updatePhase = 'idle'
        this.firmwareBuffer = new Uint8Array(0)
      } else if (messageIncludes(error, GCOME_AP_BOOT_AUTH_REQUIRED)) {
        holdForBootAuth = true
        updateError =
          error instanceof DeviceError
            ? error
            : new DeviceError(DeviceErrorCode.OPEN_FAILED, String(error))
      } else {
        const message = error instanceof Error ? error.message : String(error)
        updateError = new DeviceError(
          DeviceErrorCode.WRITE_FAILED,
          `GCOME_AP firmware update failed: ${message}`,
          error,
        )
        if (this.updatePhase !== 'in_boot') {
          this.updatePhase = 'idle'
        }
      }
    } finally {
      if (!holdForBootAuth) {
        // 恢复路径主通道即 updateDevice，勿 release 掉列表里的设备实例
        if (resumeFromBoot && this.updateDevice === this.device) {
          this.updateDevice = null
        } else {
          updateError = await this.releaseUpdateDevice(updateError)
        }
      }
    }

    if (updateError) throw updateError
  }

  // 获取固件附件下载地址
  async getFirmwareAttachmentUrl(type: string): Promise<string> {
    const device = await this.getVersionInfo()
    const fileName = device[`${type}` as keyof RemoteDeviceVersion] ?? ''
    const model = this.device.manifest.mainControl ?? ''
    const timestamp = Date.now()
    const attachmentUrl = `${this.device.getFirmwareBaseUrl()}/${model}/${fileName}?t=${timestamp}`
    return attachmentUrl
  }

  /** 擦除 → 写入 → 校验 → 成功 → 结束 */
  private async runIapFlashPipeline(): Promise<void> {
    this.reportProgress(10)
    await this.eraseDeviceFlash()
    this.reportProgress(15)
    await this.writeDeviceFlashData()
    await this.verifyDeviceFlashData()
    await this.successDeviceUpdate()
    // 0x84 后设备常立刻切回 APP，Boot 通道断开；结束失败不应把已成功写入判成升级失败
    try {
      await this.endDeviceUpdate()
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (
        /Device update end|end device update/i.test(message) &&
        /no response|timeout/i.test(message)
      ) {
        console.warn('[GCOME_AP] end after success with no response — treating as OK', error)
      } else {
        throw error
      }
    }
    this.reportProgress(100)
  }

  private async promptFirmwareAuth(request: {
    kind: 'confirmEnterBoot' | 'authorizeBootDevice'
    boot: { label: string }
    allowCancel: boolean
  }): Promise<void> {
    const hooks = getFirmwareHostHooks()
    if (!hooks) {
      // 无 UI 宿主（单元测试等）：自动继续；authorize 阶段仍依赖 openDevice
      if (request.kind === 'authorizeBootDevice') {
        console.warn(
          '[GCOME_AP] FirmwareHostHooks 未注册，跳过授权弹窗（请在 Web 启动时 setFirmwareHostHooks）',
        )
      }
      return
    }
    await hooks.requestFirmwareAuth(request)
  }

  /**
   * 从本机 manifest 的 update filter 读取 Bootloader VID/PID。
   * 禁止写死某型号数值；缺配置则失败（不可回退 APP 身份，擦除会写错）。
   */
  private resolveBootIdentityFromManifest(): { vendorId: number; productId: number } {
    const updateFilter = this.device.manifest.hardware.filters?.find((f) => f.type === 'update')
    if (updateFilter?.vendorId === undefined || updateFilter.productId === undefined) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'GCOME_AP update filter 缺少 Bootloader vendorId/productId（须写在设备 manifest，不可硬编码）',
      )
    }
    const productId = Array.isArray(updateFilter.productId)
      ? updateFilter.productId[0]
      : updateFilter.productId
    if (typeof productId !== 'number') {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'GCOME_AP update filter productId 无效')
    }
    return { vendorId: updateFilter.vendorId, productId }
  }

  /**
   * 弹窗蓝色示意名：优先 manifest update.productName（如 Volt* Upgrader），不含 VID/PID。
   */
  private resolveBootPromptLabel(): string {
    const filters = this.device.manifest.hardware.filters ?? []
    const updateFilter = filters.find((f) => f.type === 'update')
    const fromFilter = updateFilter?.productName?.trim()
    if (fromFilter) return fromFilter
    return 'Volt* Upgrader'
  }

  private async releaseUpdateDevice(updateError: DeviceError | null): Promise<DeviceError | null> {
    const updateDevice = this.updateDevice
    this.updateDevice = null
    if (!updateDevice) return updateError

    try {
      await this.device.session.channels.releaseDevice(updateDevice)
    } catch (error) {
      console.warn('[GCOME_AP] Failed to release firmware update device', error)
      if (!updateError) {
        const message = error instanceof Error ? error.message : String(error)
        return new DeviceError(
          DeviceErrorCode.WRITE_FAILED,
          `GCOME_AP firmware update cleanup failed: ${message}`,
          error,
        )
      }
    }
    return updateError
  }

  // ============== 私有协议方法 ==============

  /**
   * 进 Boot 后轮询打开 update 通道。
   * requestIfMissing=false：只查已授权列表（异步阶段无手势，弹窗无效）。
   */
  private async openUpdateDeviceWithRetry(
    options: { requestIfMissing: boolean } = { requestIfMissing: false },
  ): Promise<BaseDevice | null> {
    const attempts = options.requestIfMissing ? 3 : 6
    const intervalMs = 1000
    for (let i = 0; i < attempts; i++) {
      const updateDevice = await this.device.session.channels.openDevice(
        { usageType: 'update' },
        { requestIfMissing: options.requestIfMissing },
      )
      if (updateDevice) return updateDevice
      console.warn(`[GCOME_AP] open update channel attempt ${i + 1}/${attempts} failed, retry…`)
      await delay(intervalMs)
    }
    return null
  }

  /**
   * 进入 Boot：buffer[0]=0x5f, buffer[1]=0x06, buffer[4]=0x01, buffer[8]=0x01。
   *
   * 该命令在 APP 控制口发 64 字节原始输出报告（非 AP 帧），发送后设备重启并
   * 以 Bootloader 身份重枚举（例：VT68 → `0x19F5:0xAF7A` Volt* Upgrader），
   * 因此不等待应答。
   */
  private async enterBootLoader(): Promise<void> {
    console.log('[GCOME_AP] enter BootLoader')
    const buf = new Uint8Array(GCOME_AP_IAP.enterBootPacketSize)
    buf[0] = GCOME_AP_IAP.enterBoot
    buf[1] = 0x06
    buf[4] = 0x01
    buf[8] = 0x01

    await this.device.channel.sendOutputReport(GCOME_AP_REPORT.defaultReportId, buf)
    // 等待 Bootloader 以独立 VID/PID 重枚举，避免后续 openDevice 抢跑
    await delay(3000)
    console.log('[GCOME_AP] enterBootLoader sent, device may reboot')
  }

  /**
   * 解析 Bootloader VID/PID：优先已打开的 update 通道 deviceInfo，否则读 manifest update filter。
   * 不回退 APP VID/PID，也不写死芯片默认值。
   */
  private resolveBootIdentity(): { vendorId: number; productId: number } {
    const info = this.updateDevice?.channel.deviceInfo
    if (info && typeof info.vendorId === 'number' && typeof info.productId === 'number') {
      return { vendorId: info.vendorId, productId: info.productId }
    }
    return this.resolveBootIdentityFromManifest()
  }

  /**
   * 擦除命令（0x81）：buffer[1]=命令头长度(0x07)，buffer[2..5]=boot vid/pid（低字节在前），
   * buffer[6]=设备类型（0 键盘设备；1 RF 设备）。
   *
   * boot vid/pid 必须是 Bootloader USB 身份（来自 update 通道或 manifest update filter），不是 APP PID。
   */
  private async eraseDeviceFlash(): Promise<void> {
    console.log('[GCOME_AP] erase device flash')
    const { vendorId, productId } = this.resolveBootIdentity()

    const buf = new Uint8Array(GCOME_AP_IAP.packetSize)
    buf[0] = GCOME_AP_IAP.eraseFlash
    buf[1] = GCOME_AP_IAP.eraseHeaderLength
    buf[2] = vendorId & 0xff
    buf[3] = (vendorId >> 8) & 0xff
    buf[4] = productId & 0xff
    buf[5] = (productId >> 8) & 0xff
    buf[6] = GCOME_AP_IAP.deviceTypeKeyboard

    await this.sendIapCommand('Erase flash', buf)
  }

  /**
   * 写入命令（0x80）：buffer[1]=写入数据的长度(32)，buffer[2..5]=地址（低字节在前），
   * buffer[6..] = 32 字节数据。地址按字节递增。
   */
  private async writeDeviceFlashData(): Promise<void> {
    console.log('[GCOME_AP] write device flash data')
    const total = this.firmwareBuffer.length
    let offset = 0
    let addr = this.startAddress

    while (offset < total) {
      const chunkSize = Math.min(GCOME_AP_IAP.dataChunk, total - offset)
      const chunk = this.firmwareBuffer.slice(offset, offset + chunkSize)

      const buf = new Uint8Array(GCOME_AP_IAP.packetSize)
      buf[0] = GCOME_AP_IAP.writeFlash
      buf[1] = chunkSize
      buf[2] = addr & 0xff
      buf[3] = (addr >> 8) & 0xff
      buf[4] = (addr >> 16) & 0xff
      buf[5] = (addr >> 24) & 0xff
      buf.set(chunk, GCOME_AP_IAP.headerSize)

      await this.sendRawPacket(buf)
      // 写入时允许单包超时（跳过检查，继续下一包）
      if (this.lastResponse === null) {
        console.warn(`Write packet at offset ${offset} timeout, skipping check`)
      } else if (this.lastResponse[1] !== 0) {
        throw new DeviceError(
          DeviceErrorCode.INVALID_PARAM,
          `Write flash data failed: error code 0x${this.lastResponse[1].toString(16)}`,
        )
      }

      offset += chunkSize
      addr += chunkSize
      this.reportProgress(15 + Math.floor((offset / total) * 45))
    }

    this.reportProgress(60)
  }

  /**
   * 校验命令（0x82）：与写入命令布局一致，数据为已写入的固件内容。
   */
  private async verifyDeviceFlashData(): Promise<void> {
    console.log('[GCOME_AP] verify device flash data')
    const total = this.firmwareBuffer.length
    let offset = 0
    let addr = this.startAddress

    while (offset < total) {
      const chunkSize = Math.min(GCOME_AP_IAP.dataChunk, total - offset)
      const chunk = this.firmwareBuffer.slice(offset, offset + chunkSize)

      const buf = new Uint8Array(GCOME_AP_IAP.packetSize)
      buf[0] = GCOME_AP_IAP.verifyFlash
      buf[1] = chunkSize
      buf[2] = addr & 0xff
      buf[3] = (addr >> 8) & 0xff
      buf[4] = (addr >> 16) & 0xff
      buf[5] = (addr >> 24) & 0xff
      buf.set(chunk, GCOME_AP_IAP.headerSize)

      await this.sendRawPacket(buf)
      // 校验时允许单包超时，跳过
      if (this.lastResponse === null) {
        console.warn(`Verify packet at offset ${offset} timeout, skipping check`)
      } else if (this.lastResponse[1] !== 0) {
        throw new DeviceError(
          DeviceErrorCode.INVALID_PARAM,
          `Verify flash data failed: error code 0x${this.lastResponse[1].toString(16)}`,
        )
      }

      offset += chunkSize
      addr += chunkSize
      this.reportProgress(60 + Math.floor((offset / total) * 35))
    }

    this.reportProgress(95)
  }

  /**
   * 成功命令（0x83）：buffer[1]=0x01，buffer[2..5]=0x00。
   */
  private async successDeviceUpdate(): Promise<void> {
    console.log('[GCOME_AP] success device update')
    const buf = new Uint8Array(GCOME_AP_IAP.packetSize)
    buf[0] = GCOME_AP_IAP.success
    buf[1] = 0x01
    buf[2] = 0x00
    buf[3] = 0x00
    buf[4] = 0x00
    buf[5] = 0x00

    await this.sendIapCommand('Device update success', buf)
  }

  /**
   * 结束命令（0x84）：buffer[1]=0x01，buffer[2..5]=0x00。
   *
   * 设备收到后通常立刻退出 Boot 并以 APP 身份重枚举，Boot HID 会断开，
   * 因此经常无应答；只发一次，无应答视为成功（有应答则仍校验 error code）。
   * 禁止重试：若首次已触发重启，重发会打到已断开的 Upgrader。
   */
  private async endDeviceUpdate(): Promise<void> {
    console.log('[GCOME_AP] end device update')
    const buf = new Uint8Array(GCOME_AP_IAP.packetSize)
    buf[0] = GCOME_AP_IAP.end
    buf[1] = 0x01
    buf[2] = 0x00
    buf[3] = 0x00
    buf[4] = 0x00
    buf[5] = 0x00

    await this.sendRawPacket(buf)
    if (this.lastResponse === null) {
      console.log('[GCOME_AP] end: no response (device likely rebooting to APP) — OK')
      return
    }
    if (this.lastResponse[1] !== 0) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `Device update end: error code 0x${this.lastResponse[1].toString(16)}`,
      )
    }
  }

  // ============== 辅助方法 ==============

  /**
   * 发送 IAP 命令并校验应答（超时重试一次）。
   * 应答 buffer[1] 不等于 0 表示校验失败/错误码。
   */
  private async sendIapCommand(stage: string, buf: Uint8Array): Promise<void> {
    await this.sendRawPacket(buf)
    if (this.lastResponse === null) {
      // 重试一次
      await this.sendRawPacket(buf)
    }
    if (this.lastResponse === null) {
      throw new DeviceError(DeviceErrorCode.TIMEOUT, `${stage}: no response after retry`)
    }
    if (this.lastResponse[1] !== 0) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `${stage}: error code 0x${this.lastResponse[1].toString(16)}`,
      )
    }
  }

  /**
   * 发送 Boot 态 IAP 包（512 字节 HID 报告）并尝试接收应答。
   * 超时后 this.lastResponse 将被设为 null（不会抛出异常）。
   */
  private async sendRawPacket(data: Uint8Array): Promise<void> {
    if (!this.updateDevice) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Update device not found')
    }
    const updateDevice = this.updateDevice
    this.lastResponse = null
    const response = await this.sendAndWaitForInputReport(
      updateDevice,
      data,
      GCOME_AP_REPORT.defaultResponseReportId,
      GCOME_AP_REPORT.timeoutMs * 60,
    )
    await delay(2)

    if (response === null) {
      this.lastResponse = null
      console.warn('[GCOME_AP] No response received within timeout')
    } else {
      // Boot 应答至少需 2 字节（status）；完整报告多为 512 字节
      if (response.byteLength < 2) {
        throw new DeviceError(
          DeviceErrorCode.READ_FAILED,
          `GCOME_AP IAP response is too short: expected at least 2 bytes, received ${response.byteLength}`,
        )
      }
      this.lastResponse = new Uint8Array(response.buffer, response.byteOffset, response.byteLength)
    }
  }

  /**
   * 先注册 Input Report 监听再发送，避免设备同步回包时丢失应答。
   * 发送失败、收到目标报告或超时时都会注销监听器。
   */
  private sendAndWaitForInputReport(
    updateDevice: BaseDevice,
    data: Uint8Array,
    reportId: number,
    timeout: number,
  ): Promise<DataView | null> {
    return new Promise((resolve, reject) => {
      let settled = false

      const cleanup = () => {
        clearTimeout(timer)
        updateDevice.channel.offInputReport(handler)
      }
      const finish = (response: DataView | null) => {
        if (settled) return
        settled = true
        cleanup()
        resolve(response)
      }
      const fail = (error: unknown) => {
        if (settled) return
        settled = true
        cleanup()
        reject(error)
      }
      const timer = setTimeout(() => {
        finish(null)
      }, timeout)

      const handler = (id: number, data: DataView) => {
        if (id === reportId) {
          finish(data)
        }
      }

      try {
        updateDevice.channel.onInputReport(handler)
        updateDevice.channel.sendOutputReport(GCOME_AP_REPORT.defaultReportId, data).catch(fail)
      } catch (error) {
        fail(error)
      }
    })
  }

  private reportProgress(progress: number) {
    this.device.emit(FirmwareEvents.WritedData, { progress: Math.floor(progress) })
  }
}
